import { getUserData } from './API';
import device from './device/device';
import { getActivityStatus } from './displayStatus';
import {userTypes} from "Constants/constants";
import versionCompare from 'semver-compare';
import * as Sentry from '@sentry/browser';
import { isiPad } from './environment';
import { AssessmentAssignmentTypes } from '../constants/constants';
import { FEATURE_FLAGS } from '../constants/featureFlags';
import { ASSIGNMENT_TYPE } from './assignment/constants';
import { isBTSEffective } from './entitlementService';

/**
 * Wraps a promise with a timeout, rejecting if the operation takes too long.
 * Properly cleans up the timeout when the promise resolves to prevent memory leaks.
 * 
 * @param {Promise} promise - The promise to wrap with a timeout
 * @param {number} timeoutMs - Timeout duration in milliseconds
 * @param {string} errorMessage - Optional custom error message. If not provided, defaults to a generic timeout message
 * @returns {Promise} A promise that resolves with the original promise's value or rejects with a timeout error
 * 
 * @example
 * const result = await withTimeout(
 *   fetchData(),
 *   5000,
 *   'Data fetch timeout after 5s'
 * );
 */
export function withTimeout(promise, timeoutMs, errorMessage) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error(errorMessage || `Operation timeout after ${timeoutMs}ms`)),
      timeoutMs
    );
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
}

/**
 * Calculate a timeout for speech/audio playback based on word count heuristics.
 * Uses average speaking rate to estimate duration and adds configurable buffer.
 * 
 * @param {string} speechText - The text to be spoken (may contain SSML tags)
 * @param {number} bufferMs - Additional buffer time in milliseconds (default: 10000ms)
 * @param {number} minTimeoutMs - Minimum timeout in milliseconds (default: 30000ms)
 * @returns {number} Calculated timeout in milliseconds
 */
export function calculateSpeechTimeout(speechText, bufferMs = 10000, minTimeoutMs = 30000) {
  if (!speechText) return minTimeoutMs;
  
  // Strip SSML tags and clean the text to get actual spoken content
  const cleanText = speechText
    .replace(/<[^>]*>/g, ' ') // Remove all tags
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
  
  // Count words
  const wordCount = cleanText.split(' ').filter(word => word.length > 0).length;
  
  // Average speaking rate: ~140 words per minute for narration/instruction
  // For children's content, we use a slightly slower rate than typical adult speech
  const WORDS_PER_MINUTE = 140;
  const estimatedDurationMs = (wordCount / WORDS_PER_MINUTE) * 60 * 1000;
  
  // Add buffer for network delays and processing
  const timeoutWithBuffer = estimatedDurationMs + bufferMs;
  
  // Ensure minimum timeout
  const finalTimeout = Math.max(timeoutWithBuffer, minTimeoutMs);
  
  return Math.round(finalTimeout);
}
export function formatAREAScore(score, long=false, veryshort=false){ // TODO pass these props more cleanly
  let years = Math.floor(score);
  let months = Math.round((score - years) * 12);
  if(long){
    return (years + " year, " + months + " month");
  }
  if(veryshort) {
    return(years + "y, " + months + "m");
  }
  return(years + " y, " + months + " m");
}

// TODO dedup this function
export function uuidToHEX(value){
  if (value.length < 30 && !isNaN(Number(value))) {
    // If the value is a number and less than 30 characters, it is an OID and not UUID. Return as is.
    return value;
  }

  const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if(pattern.test(value)){
    value = value.replace(/\-/g, '').toUpperCase();
  }
  return value;
}

export function formatGuidForSIS(guid){
  if (guid.length < 30 && !isNaN(Number(guid))) {
    // If the value is a number and less than 30 characters, it is an OID and not UUID. Return as is.
    return guid;
  }

  let formattedGuid = guid.toLowerCase();

  //Check for valid UUID and fix if not
  const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if(pattern.test(formattedGuid) === false){
    formattedGuid = formattedGuid.replace(/(\w{8})(\w{4})(\w{4})(\w{4})(\w{12})/, "$1-$2-$3-$4-$5");
  }
  return formattedGuid;
}

export function makeStringPosessive(nameString){
  return nameString + (nameString[nameString.length -1] == 's' ? "'" : "'s");
}

//arrToSort - [Array]
//asc - [Boolean]
// order the activities by status. Completed first, then errors, then processing, then assigned
export function sortByStatus(arrToSort, asc){
  let result = arrToSort.sort((element1, element2) => {
    let status1 = element1.assessmentStatus ? element1.assessmentStatus.id : getActivityStatus(element1).id;
    let status2 = element2.assessmentStatus ? element2.assessmentStatus.id : getActivityStatus(element2).id;
    if (status1 == status2) {
      if(element1.updatedAt && element2.updatedAt){
        return element2.updatedAt - element1.updatedAt;
      }else if(element1.date && element2.date) {
        return new Date(element1.date).getTime() - new Date(element2.date).getTime();
      }
    } else if(status1 == "ERROR"){
      return -1;
    } else if(status1 == "ABORTED" && status2 != "ERROR"){
      return -1;
    } else if(status1 == "COMPLETE" && status2 != "ERROR" && status2 != "ABORTED"){
      return -1;
    } else if(status1 == "RESCORED" &&  status2 != "ERROR" && status2 != "ABORTED" && status2 != "COMPLETE"){
      return -1;
    } else if(status1 == "SCORING" &&  status2 != "ERROR" && status2 != "ABORTED" && status2 != "COMPLETE" && status2 != "RESCORED"){
      return -1;
    } else if(status1 == "IN_PROGRESS" &&  status2 != "ERROR" && status2 != "ABORTED" && status2 != "COMPLETE" && status2 != "RESCORED" && status2 != "SCORING"){
      return -1;
    } else if(status1 == "ASSIGNED" &&  status2 != "ERROR" && status2 != "ABORTED" && status2 != "COMPLETE" && status2 != "RESCORED" && status2 != "SCORING" && status2 != "IN_PROGRESS"){
      return -1;
    } else if(status1 == "UNASSESSED" &&  status2 != "ERROR" && status2 != "ABORTED" && status2 != "COMPLETE" && status2 != "RESCORED" && status2 != "SCORING" && status2 != "IN_PROGRESS" && status2 != "ASSIGNED"){
      return -1;
    } else {
      return 1;
    }
  });

  return asc ? result : result.reverse();
}

/**
 * Escape XML-significant characters so a raw string can be safely interpolated
 * inside an SSML `<speak>…</speak>` payload. Item-bank text is controlled, but a
 * stray `&` or `<` would otherwise produce malformed SSML.
 * @param {string} str
 * @returns {string} the escaped string
 */
export function escapeSsml(str){
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Takes a string, stripes the <speak>, <prosody> tags, then create a array of objects in the form of { type, value } to be used for speech.
 * @param {string} speechString A string to be spoken, only strings containing <phoneme ... /> will be processed.
 * @returns An array of objects in the form of { type, value }, returns an empty array if there is no Phonemes in the string.
 */
export function parseTextWithPhonemes(speechString, skipTranslation){
  const replacedFancyQuotes = speechString.replace(/[“”]/g, '"'); 

    if(!stringContainsHumanPhoneme(replacedFancyQuotes) && !stringContainsSyllable(replacedFancyQuotes)) return []

    const phonemeRegex = /(<phoneme(?:\s+voice="human")?\s+alphabet="ipa" ph="['ˈa-zA-Z\u0250-\u02AF\/\æ\ð\ŋ\θ]+"\/>)/g
    const phonemeValueRegex = /(<phoneme(?:\s+voice="human")?\s+alphabet="ipa" ph="(['ˈa-zA-Z\u0250-\u02AF\/\æ\ð\ŋ\θ]+)"\/>)/g // with an inner group for the phoneme value

    // Syllable tags (Evaluar / es-mx) carry orthographic Spanish text, e.g. <syllable ph="bús"/>
    const syllableRegex = /(<syllable\s+ph="[^"]+"\s*\/>)/g
    const syllableValueRegex = /<syllable\s+ph="([^"]+)"\s*\/>/ // inner group for the syllable value
    // Strings with NO <syllable> tag — every en-us string, plus es-mx non-blending —
    // take the ORIGINAL phoneme-only split, byte-for-byte the pre-syllable behavior, so
    // English is provably untouched. Only a string that actually carries a <syllable>
    // tag uses the combined regex (whose 2nd capture group interleaves `undefined`,
    // skipped below).
    const splitRegex = stringContainsSyllable(replacedFancyQuotes)
      ? new RegExp(phonemeRegex.source + '|' + syllableRegex.source, 'g')
      : phonemeRegex

    // clean up the string by removing <speak>, <prosody> tags
    const removedSpeakTags = removeSpeakTags(replacedFancyQuotes)
    const removedSpeedTags = removeProsodyTags(removedSpeakTags)
    const removedEmphasisTags = removeEmphasisTags(removedSpeedTags);
    const splitSpeechString = removedEmphasisTags.split(splitRegex);

    // construct an array of object with property { type, value }
    const array = []
    while(splitSpeechString.length){

      const rawItem = splitSpeechString.shift()
      if(rawItem == null) continue // split interleaves undefined for the non-matching capture group
      const item = rawItem.trim()
      if(!item) continue // ignore empty strings
      let test = removeBreakTags(item);
      test = removeAllPunctuation(test);
      if(!test || !test.trim().length) continue // ignore string with only break tags
      // --- possibly need to ignore some other tags as the only thing in the string ---

      const isPhoneme = item.match(phonemeRegex)
      const isSyllable = !isPhoneme && item.match(syllableRegex)

      let type = "text"
      let value = `<speak>${removeLeadingPunctuation(removeTrailingPunctuation(item))}</speak>`
      if(isPhoneme){
        type = "phoneme"
        value = [...item.matchAll(phonemeValueRegex)][0][2]
      }else if(isSyllable){
        type = "syllable"
        value = item.match(syllableValueRegex)[1]
      }
      array.push({ type, value, skipTranslation })
    }
    return array
}

/**
 * Used to check if a string contains a correctly formatted phoneme tag to use human voice,
 * a phoneme for human voice string has a strict format, the order of the attributes matters.
 * @param {string} speechString a string to check for phoneme to use human voice
 * @returns the match or null
 */
export function stringContainsHumanPhoneme(speechString){
  const humanPhonemeRegex = /(<phoneme(?:\s+voice="human")?\s+alphabet="ipa" ph="['ˈa-zA-Z\u0250-\u02AF\/\æ\ð\ŋ\θ]+"\/>)/g
  return speechString ? speechString.match(humanPhonemeRegex) : null;
}

/**
 * Checks whether a string contains a syllable tag, e.g. <syllable ph="bús"/>.
 * Used (alongside phonemes) to decide if the string needs pre-recorded audio.
 * @param {string} speechString a string to check for a syllable tag
 * @returns the match or null
 */
export function stringContainsSyllable(speechString){
  const syllableRegex = /(<syllable\s+ph="[^"]+"\s*\/>)/g
  return speechString ? speechString.match(syllableRegex) : null;
}

/**
 * Used to clean up a string with leading punctuation and space characters
 * @param {string} string any string
 * @returns
 */
export function removeLeadingPunctuation(string) {
  let output = string.trim();

  // Replace punctuation at the start of the string
  const nonWordCharRegexFirstChar = /^[\.\,\!\?\s]*/g;
  output = output.replace(nonWordCharRegexFirstChar, "");

  // Match punctuation or spaces after an SSML tag at start of string
  const nonWordCharRegexAfterSSML = /^(<[^>]+>)[\.\,\!\?\s]+/g;
  return output.replace(nonWordCharRegexAfterSSML, "$1");
}

/**
 * Used to clean up a string with trailing punctuation and space characters
 * @param {string} string any string
 * @returns
 */
export function removeTrailingPunctuation(string) {
  let output = string.trim();

  // Replace punctuation at the end of the string
  const nonWordCharRegexLastChar = /[\.\,\!\?\s]*$/g;
  output = output.replace(nonWordCharRegexLastChar, "");

  // Match punctuation or spaces before an SSML tag at end of string
  const nonWordCharRegexBeforeSSML = /[\.\,\!\?\s]+(?=<[^>]+>$)/g;
  return output.replace(nonWordCharRegexBeforeSSML, "");
}

/**
 * Used to remove any <speak>...</speak> tags from a string
 * @param {string} string any string
 * @returns string without the <speak> tags
 */
export function removeSpeakTags(string){
  const speakTagRegex = /<\/*speak>/g
  return string.replace(speakTagRegex, "");
}

/**
 * Checks whether a locale belongs to an English dialect.
 * @param {string} locale locale string, such as en-us or en-gb
 * @returns {boolean} true when the normalized locale starts with en
 */
export function isEnglishLocale(locale) {
  return String(locale || '').toLowerCase().startsWith('en');
}

/**
 * Checks whether a TTS string represents one spoken word.
 * Strips common SSML wrappers, then fails only when whitespace separates multiple tokens.
 * Punctuation and inner SSML around a single word still pass (e.g. cat., "cat.", <prosody>cat</prosody>).
 * @param {string} text TTS text that may include speak tags, quotes, and punctuation
 * @returns {boolean} true when the stripped text contains a single spoken word
 */
export function isSingleSpokenWord(text) {
  if (!text) return false;

  const STRIPPED_BOUNDARY_PUNCTUATION_REGEX = /^[\s"'“”‘’.,!?;:]+|[\s"'“”‘’.,!?;:]+$/g;

  let strippedText = removeSpeakTags(text);
  strippedText = removeProsodyTags(strippedText) || '';
  strippedText = removeEmphasisTags(strippedText) || '';
  strippedText = removeBreakTags(strippedText) || '';
  strippedText = strippedText.replace(/<[^>]+>/g, '');

  const spokenParts = strippedText.trim().split(/\s+/).filter(part => part.length > 0);
  if (spokenParts.length !== 1) {
    return false;
  }

  const wordContent = spokenParts[0].replace(STRIPPED_BOUNDARY_PUNCTUATION_REGEX, '');
  return wordContent.length > 0;
}

/**
 * Used to remove any <prosody>...</prosody> tags from a string
 * @param {string} string any string
 * @returns string without the prosody tags
 */
export function removeProsodyTags(string) {
  if (!string) return;

  const prosodyTagRegex = /<\/?prosody\b[^>]*>/g;
  return string.replace(prosodyTagRegex, "");
}

/**
 * Used to remove any <break/> tags from a string
 * @param {string} string any string
 * @returns string without the break tags
 */
export function removeBreakTags(string) {
  if (!string) return;

  const breakTagRegex = /<\/?break\b[^>]*>/g;
  return string.replace(breakTagRegex, "");
}

/**
 * Used to remove an opening <break/> tag when it appears before all other content.
 * Strings wrapped in <speak> keep inner break tags so single-word detection stays conservative.
 * @param {string} string any string
 * @returns string without the break tags
 */
export function removeOpeningBreakTag(string) {
  if (!string) return;

  // Match <break> tag only if it appears at the very beginning of the string
  const breakTagAtStartRegex = /^<break\b[^>]*>/;
  return string.replace(breakTagAtStartRegex, "");
}

export function removeOpeningEllipsis(string) {
  if (!string) return;

  // Match an ellipsis "..." only if it appears at the very beginning of the string
  const ellipsisAtStartRegex = /^\.\.\./;
  return string.replace(ellipsisAtStartRegex, "");
}

/**
 * Used to remove any <emphasis/> tags from a string
 * @param {string} string any string
 * @returns string without the break tags
 */
export function removeEmphasisTags(string) {
  if (!string) return;

  const breakTagRegex = /<\/?emphasis\b[^>]*>/g;
  return string.replace(breakTagRegex, "");
}

export function removeAllPunctuation(string) {
  if (!string) return;

  const punctuationRegex = /[.,#!$%&;:{}=\-_`~()?'"“”]/g;
  return string.replace(punctuationRegex, '');
}

export function parseSkipTranslationTags(speechString){
  let cleanSpeechString = speechString.replace(/<\/*speak>/g, "");
  let splitSpeechString = cleanSpeechString.split(/<mark [a-zA-Z|\s|=|"|:]*skipTranslation[a-zA-Z|\s|=|"|:]*\/>/g);
  let parsedSpeechText = [];
  for(let i = 0; i < splitSpeechString.length; i++){
    let textToSpeak = splitSpeechString[i];
    if(textToSpeak && textToSpeak != ""){
      // Remove trailing commas and periods from the text
      textToSpeak = textToSpeak.replace(/[,.]*$/, "");
      parsedSpeechText.push({
        textToSpeak: textToSpeak,
        skipTranslation: i%2 == 1,
      })
    }
  }
  return parsedSpeechText;
}

export function isDefined(value) {
  return value !== undefined && value !== null;
}

// takes an array of activity objects and returns that array sorted by date created, most recent first
export function sortActivitiesByDate(activities){
  let compare = function (activity1,activity2) {
    let date1 = new Date(activity1.updatedAt);
    let date2 = new Date(activity2.updatedAt);
    if (date1.getTime() < date2.getTime()){
      return 1;
    } else if (date1.getTime() > date2.getTime()){
      return -1;
    } else {
      return 0;
    }
  }
  return activities.sort(compare);
}

//sort activities by descending createdAt date
export function sortActivitiesByCreatedDate(activities){
  return activities.sort((a, b) => {
    const aDate = new Date(a.createdAt || 0);
    const bDate = new Date(b.createdAt || 0);
    return bDate - aDate;
  });
}

export function formatAsOrdinal(i) {
  var j = i % 10,
      k = i % 100;
  if (j == 1 && k != 11) {
      return i + "st";
  }
  if (j == 2 && k != 12) {
      return i + "nd";
  }
  if (j == 3 && k != 13) {
      return i + "rd";
  }
  return i + "th";
}

export function containsNonAlpha(str){
  return !/^[a-z]+$/i.test(str);
}

export function isAdminUser(userType) {
  return userType === userTypes.DISTRICT_ADMIN || userType === userTypes.SCHOOL_ADMIN;
}

export function isBrowser() {
  // window.device is not always defined immediately on application launch on iOS.
  // This was moved from a constant in constants.js to a computed value in util.js so that it would only be accessed after the deviceready event in index.js.
  return !window.device || window.device.platform === 'browser'; // TODO: Move this to an env var
}

export function isCordova(){
  return window.cordova != null;
}

export function delay(duration) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, duration);
  });
}

export function getSentryEnv(){
  if(!isProductionEnv()) return "development"
  // To lower case to make sure we don't create a new production environment in sentry.
  return process.env.STACK.toLocaleLowerCase()
}

/**
 * Logs an exception to Sentry with structured error information.
 * 
 * For better error grouping and searchability in Sentry, the first parameter should be
 * a new Error object with a descriptive, static title that identifies the code path
 * (e.g., "Story fetch failure", "Word intervention failure"). The original error details
 * should be passed in the extraData parameter.
 * 
 * @param {Error|Object} error - Error object with a descriptive, static message identifying the code path.
 *   For best results, use: `new Error("Descriptive static title")` rather than passing the original error.
 *   Examples: `new Error("Story fetch failure")`, `new Error("Microphone access failure")`
 * @param {string} [infoMessage] - Additional context message added as a breadcrumb
 * @param {Object} [extraData] - Additional data to include in Sentry breadcrumbs.
 *   When logging an original error, include: `{ originalError: err?.message, originalStack: err?.stack }`
 * @param {Object} [hintContext] - Optional Sentry hint context for additional metadata (e.g., tags)
 * 
 * @example
 * // Recommended pattern for better Sentry grouping:
 * try {
 *   await fetchStory();
 * } catch (err) {
 *   logSentryException(
 *     new Error("Story fetch failure"),
 *     "Error getting story.",
 *     { originalError: err?.message, originalStack: err?.stack }
 *   );
 * }
 * 
 * @example
 * // For errors without an original error object:
 * logSentryException(
 *   new Error("App sounds playback timeout"),
 *   "playAppSounds timeout"
 * );
 */
export function logSentryException(error, infoMessage, extraData, hintContext = undefined) {
  // First, check if error is an Error object. If not, then create a new error with the infoMessage.
  // If using the infoMessage, check if the error objects passed in is a graphql type error and log that data in a breadcrumb
  if (infoMessage) {
    Sentry.addBreadcrumb({
      message: infoMessage,
      level: "error",
    });
  }

  if (extraData) {
    Sentry.addBreadcrumb({
      message: 'Extra data',
      level: "error",
      data: sanitizeDataForSentry(extraData)
    });
  }

  if (error instanceof Error || (error && error.stack && error.message)) {
    hintContext ? Sentry.captureException(error, hintContext) : Sentry.captureException(error);
  } else {
    if (error && error.errors) {
      const data = {
        graphqlErrors: JSON.stringify(error.errors),
      }

      if (error.data) {
        data.graphqlData = JSON.stringify(error.data);
      }

      Sentry.addBreadcrumb({
        message: 'GraphQL error',
        level: "error",
        data,
      });
    }

    hintContext ? Sentry.captureException(new Error(infoMessage || 'Unknown error'), hintContext) : Sentry.captureException(new Error(infoMessage || 'Unknown error'));
  }
}

export function isNullOrUndefined(value) {
  return value === null || value === undefined;
}

export function isNotNullOrUndefined(value) {
  return value !== null && value !== undefined;
}

/**
 * Howler defaults to Web Audio API and falls back to HTML5 Audio if it is not supported.
 * Versions that do not have Web Audio API support
 * Pre-iOS 14.5 (Good with Howler automatic fallback to HTML5 audio)
 * Versions that have problem with HTML5 audio
 * iOS 15.4 (Good with Howler default with Web Audio API)
 * Versions that have problem with Web Audio API (AudioContext)
 * iOS 16.4.1 - 16.5 current as of June 2023 (Implemented Howler force HTML5 audio)
 * TODO: Limit forcing HTML5 audio when iOS fixes the Web Audio API bug by limiting to the fixed version.
 * https://docs.google.com/document/d/1vB2AHqNoBfizvZ3WGrwjE_GynlRdVb7YnILejHxRYuQ/edit#heading=h.sdghvqz7lm5d
 */
export function shouldForceHTML5Audio(){
   const version = device.getBrowserForSession().version;
   
   // Force HTML5 audio if version is unknown or invalid
   if (!version) {
    return true;
   }
   
   const isIOS16_4_or_newer = isiPad() && (versionCompare(version, "16.4.1") >= 0);

  // Detect ChromeOS
  const isChromeOS =
    navigator.userAgentData?.platform === 'Chrome OS' ||
    /\bCrOS\b/i.test(navigator.userAgent);
  
  const isChromeBookAndFullScreen = isChromeOS && isFullscreenByDimensions();

   const forceHTML5 = isIOS16_4_or_newer || isChromeBookAndFullScreen; // Force HTML5 audio for iOS 16.4.1 and newer due to Web Audio API bug, or ChromeOS in fullscreen mode for Kiosk mode
   return forceHTML5;
}

/**
 * Checks if the device is an iPad running iOS below a specified major version.
 * Used to determine audio processing constraints compatibility for older iPads.
 * 
 * @param {number} majorVersion - The major iOS version to compare against (e.g., 18)
 * @returns {boolean} True if device is an iPad running below the specified version
 */
export function isIPadBelowVersion(majorVersion) {
  if (!isiPad()) {
    return false;
  }
  
  const version = device.getBrowserForSession().version;
  
  // If version is unknown, assume it's an older device
  if (!version) {
    return true;
  }
  
  const isBelow = versionCompare(version, `${majorVersion}.0`) < 0;
  return isBelow;
}

/**
 * Checks wheether an email is valid
 * @param {string} email email to check
 * @returns {boolean} valid or not
 */
export function isValidEmail(email) {
  const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  return regex.test(String(email).toLowerCase());
}

export function getDefaultGSFWordBank() {
  return {
    "words": [
      {
        "word": "cat",
        "category": 1
      },
      {
        "word": "crab",
        "category": 1
      },
      {
        "word": "camel",
        "category": 1
      },
      {
        "word": "dog",
        "category": 1
      },
      {
        "word": "duck",
        "category": 1
      },
      {
        "word": "donkey",
        "category": 1
      },
      {
        "word": "cabinet",
        "category": 2
      },
      {
        "word": "couch",
        "category": 2
      },
      {
        "word": "counter",
        "category": 2
      },
      {
        "word": "desk",
        "category": 2
      },
      {
        "word": "drawer",
        "category": 2
      },
      {
        "word": "door",
        "category": 2
      }
    ],
    "categories": [
      {
        "id": 1,
        "description": "animals"
      },
      {
        "id": 2,
        "description": "furniture"
      }
    ]
  };
}

/**
 * Checks if the assignment type is an assessment-related type
 * @param assignmentType The type to check
 * @returns true if the type is ASSESSMENT, BENCHMARK, CALIBRATION, PROGRESS_MONITORING, SODA or FORMATIVE
 */
export function isAssessmentTypeAssignment(assignmentType) {
  // Uppercase the assignment type to match the constants no matter what / case insensitive
  const isAssessmentType = AssessmentAssignmentTypes.includes(assignmentType.toUpperCase());
  return isAssessmentType;
}

export function shouldUseAOS(assignmentId, assignmentType, manifest, isAmiraIsipPilot, isSpanishPilot = false) {
  if (!FEATURE_FLAGS.AOS && !isAmiraIsipPilot) return false;

  const nextManifestLocale = manifest?.[0]?.locale?.toLowerCase();
  const nextManifestItemNonEnglish = nextManifestLocale && 
    nextManifestLocale !== "en-us" && 
    nextManifestLocale !== "en_us";

  const isCalibrationAssignment = assignmentType === ASSIGNMENT_TYPE.CALIBRATION;
  const isSpanishLocale = nextManifestLocale === "es-mx" || nextManifestLocale === "es_mx";
  // Spanish assessments route through AOS for districts holding the BTS license (ASSMNT-1702),
  // OR when the USE_SPANISH_AOS global override is on (disabled during rollout; flipped on per
  // stack later once no school uses legacy pathways). Calibration always allowed; isSpanishPilot
  // kept for backward compatibility.
  const allowSpanishForAOS = (isBTSEffective() || FEATURE_FLAGS.USE_SPANISH_AOS) && isSpanishLocale;
  const allowNonEnglishForAOS = !nextManifestItemNonEnglish || isCalibrationAssignment || allowSpanishForAOS || isSpanishPilot;

  const shouldUseAOS = assignmentId && isAssessmentTypeAssignment(assignmentType) && allowNonEnglishForAOS;

  return shouldUseAOS;
}

/**
 * Checks if the current environment is a production environment
 * @returns {boolean} true if the environment is PRODUCTION or PROD2
 */
export function isProductionEnv() {
  return process.env.STACK === 'PRODUCTION' || 
    process.env.STACK === 'PROD2' || 
    process.env.STACK === 'PRODCA';
}

/**
 * Converts say-as tags with interpret-as="spell-out" to letter spelling format
 * For each letter in the word, creates a quoted, capitalized letter format for AI voice
 * @param {string} speechString A string that may contain spell-out tags
 * @returns {string} The string with spell-out tags converted to letter spelling format
 */
export function parseTextWithSpellOutTag(speechString) {
  if (!speechString) return speechString;

  // Regex to match say-as tags with interpret-as="spell-out"
  const spellOutRegex = /<say-as\s+interpret-as="spell-out">([^<]+)<\/say-as>/g;

  return speechString.replace(spellOutRegex, (match, word) => {
    // Split the word into individual characters
    const characters = word.split('');
    
    // Convert each character to quoted, capitalized format
    const letterSpelling = characters.map(char => {
      // If it's a letter, capitalize it and wrap in quotes
      if (/[a-zA-Z]/.test(char)) {
        return `"${char.toUpperCase()}"`;
      }
      // If it's not a letter (number, punctuation, etc.), return as is
      return char;
    });

    // Join the letters with commas and spaces
    return letterSpelling.join(', ');
  });
}

/**
 * Removes skip translation tags that wrap around phoneme tags
 * @param {string} speechString A string that may contain skip translation tags around phonemes
 * @returns {string} The string with skip translation tags removed from around phonemes
 */
export function removeSkipTranslationTagsFromPhonemes(speechString) {
  if (!speechString) return speechString;
  
  // Pattern to match skip translation tags that wrap around phoneme tags
  // This matches: <mark name="skipTranslation: start"/><phoneme.../><mark name="skipTranslation: end"/>
  const skipTranslationAroundPhonemeRegex = /<mark\s+name="skipTranslation:\s*start"\/>\s*(<phoneme[^>]*\/>)\s*<mark\s+name="skipTranslation:\s*end"\/>/g;
  
  return speechString.replace(skipTranslationAroundPhonemeRegex, '$1');
}

/**
 * Parses text that contains both skip translation tags and phoneme tags
 * @param {string} speechString A string that may contain both skip translation and phoneme tags
 * @param {boolean} skipTranslation Whether to skip translation for the overall text
 * @returns {Array} An array of objects with properties for both skip translation and phoneme handling
 */
export function parseTextWithSkipTranslationAndPhonemes(speechString, skipTranslation) {
  if (!speechString) return [];
  
  // First, clean up skip translation tags from around phonemes
  const cleanedSpeechString = removeSkipTranslationTagsFromPhonemes(speechString);
  
  // Parse skip translation tags first
  const skipTranslationSegments = parseSkipTranslationTags(cleanedSpeechString);
  
  const result = [];
  
  for (const segment of skipTranslationSegments) {
    // For each segment, check if it contains phonemes
    const phonemeItems = parseTextWithPhonemes(segment.textToSpeak, segment.skipTranslation);
    
    if (phonemeItems.length > 0) {
      // This segment contains phonemes, add them to the result
      // Remove skipTranslation property from phoneme items since tags were removed
      const phonemeItemsWithoutSkipTranslation = phonemeItems.map(item => {
        const { skipTranslation, ...itemWithoutSkipTranslation } = item;
        return itemWithoutSkipTranslation;
      });
      result.push(...phonemeItemsWithoutSkipTranslation);
    } else {
      // This segment is just text, add it as a text item
      result.push({
        type: 'text',
        value: `<speak>${segment.textToSpeak}</speak>`,
        skipTranslation: segment.skipTranslation,
      });
    }
  }
  
  return result;
}

/**
 * Determines if the assessment in progress icon should be shown.
 * Shows only when there's a forced assessment active and the user is in New Mexico state.
 * 
 * @param {boolean} forcedAssessment - Whether there's a forced assessment active
 * @returns {boolean} True if the assessment in progress icon should be displayed
 */
export function shouldShowAssessmentInProgressIcon(forcedAssessment) {
  var userData = getUserData();
  return forcedAssessment && (userData.state === "NM" || userData.amiraIsipVisibility === true);
}

/**
 * Sanitizes data by removing PII fields before sending to Sentry
 * @param {any} data - The data to sanitize (object, array, or primitive)
 * @returns {any} Sanitized data without PII
 */
export function sanitizeDataForSentry(data) {
  if (data === null || data === undefined) {
    return data;
  }
  
  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => sanitizeDataForSentry(item));
  }
  
  // Handle objects
  if (typeof data === 'object') {
    const sanitized = { ...data };
    
    // Remove PII fields (case insensitive)
    const piiFields = [
      'firstName', 'firstname', 'first_name',
      'lastName', 'lastname', 'last_name', 
      'email', 'emailAddress', 'email_address',
      'parentEmail', 'parent_email', 'parentinfo', 'parentInfo', 'parent_info',
      'teacherEmail', 'teacher_email',
      'parentPhone', 'parent_phone', 'phone',
      'parent', 'guardian',
      'name', 'fullName', 'full_name', 'displayName', 'display_name'
    ];
    
    // Remove any field that contains PII
    Object.keys(sanitized).forEach(key => {
      const lowerKey = key.toLowerCase();
      if (piiFields.some(piiField => lowerKey.includes(piiField.toLowerCase()))) {
        delete sanitized[key];
      } else if (typeof sanitized[key] === 'object') {
        // Recursively sanitize nested objects
        sanitized[key] = sanitizeDataForSentry(sanitized[key]);
      }
    });
    
    return sanitized;
  }
  
  // Return primitives as-is
  return data;
}

/**
 * Determines if the browser window is in fullscreen mode by comparing dimensions
 * @returns {boolean} Returns true if window dimensions match screen dimensions, false otherwise
 */
export function isFullscreenByDimensions() {
  return window.innerHeight === screen.height && window.innerWidth === screen.width;
}

export async function validateBlobURL (url, timeout = 3000) {
  try {
    // Create an AbortController to handle timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    const response = await fetch(url, { 
      method: 'GET',
      signal: controller.signal 
    });
    
    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    // Check if error is due to timeout/abort
    if (error.name === 'AbortError') {
      console.warn('validateBlobURL timeout after', timeout, 'ms for URL:', url);
    }
    return false;
  }
};

/**
 * Creates a valid synthetic PCM WAV blob with 100ms of silence.
 * Used to kickstart ASR connection by sending an initial audio segment without malformed audio data.
 *
 * @returns {Blob} A WAV format blob containing 100ms of silent audio
 */
export function createSilentWavBlob() {
  const SAMPLE_RATE_HZ = 16000;
  const NUM_CHANNELS = 1;
  const BITS_PER_SAMPLE = 16;
  const SILENCE_DURATION_MS = 100;

  const bytesPerSample = BITS_PER_SAMPLE / 8;
  const numSamples = Math.floor((SAMPLE_RATE_HZ * SILENCE_DURATION_MS) / 1000);
  const dataSize = numSamples * NUM_CHANNELS * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  let offset = 0;
  const writeString = (value) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
    offset += value.length;
  };

  writeString('RIFF');
  view.setUint32(offset, 36 + dataSize, true); offset += 4;
  writeString('WAVE');
  writeString('fmt ');
  view.setUint32(offset, 16, true); offset += 4; // PCM fmt chunk size
  view.setUint16(offset, 1, true); offset += 2; // PCM format
  view.setUint16(offset, NUM_CHANNELS, true); offset += 2;
  view.setUint32(offset, SAMPLE_RATE_HZ, true); offset += 4;
  view.setUint32(offset, SAMPLE_RATE_HZ * NUM_CHANNELS * bytesPerSample, true); offset += 4; // byteRate
  view.setUint16(offset, NUM_CHANNELS * bytesPerSample, true); offset += 2; // blockAlign
  view.setUint16(offset, BITS_PER_SAMPLE, true); offset += 2;
  writeString('data');
  view.setUint32(offset, dataSize, true); offset += 4;

  // Silent PCM payload (all zeros)
  for (let i = 0; i < numSamples; i += 1) {
    view.setInt16(offset, 0, true);
    offset += 2;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Classifies a failed request into a coarse category for Sentry tagging and fingerprinting.
 *
 * GT-102: captures that build a constant `new Error(...)` group every failure mode into
 * one Sentry issue, so an expired credential, a client abort and a network drop are
 * indistinguishable. Expiry is kept apart from other auth rejections (bad or missing
 * signature, invalid identity token) so the expired share is not inflated.
 *
 * @param {Error|Object|string} err - the original rejection value
 * @returns {string} one of expired, auth_rejected, client_abort, network, graphql, other.
 *   Values avoid the word "credentials"; Sentry's default scrubber has been seen to blank strings that contain it.
 */
export function classifyRequestFailure(err) {
  const gqlMessages = (err?.errors || []).map(e => e?.message).filter(Boolean);
  const haystack = [err?.code, err?.name, err?.message, ...gqlMessages, typeof err === 'string' ? err : '']
    .filter(Boolean).join(' ');
  // Narrow on purpose: a bare /expired/ would also match unrelated errors such as an expired presigned URL.
  if (/ExpiredToken|token (included in the request )?is expired|token has expired/i.test(haystack)) return 'expired';
  if (/InvalidIdentityToken|not ?authorized|unauthorized|Missing Authentication|AccessDenied/i.test(haystack)) {
    return 'auth_rejected';
  }
  if (/abort/i.test(haystack)) return 'client_abort';
  if (/Failed to fetch|Load failed|NetworkError|Network request failed/i.test(haystack)) return 'network';
  if (err?.errors) return 'graphql';
  // HTTP-shaped errors (superagent and friends) carry a status with a generic message.
  const status = Number(err?.status || err?.statusCode);
  if (status === 401 || status === 403) return 'auth_rejected';
  return 'other';
}
